import { useMemo, useState } from 'react';
import { AlertTriangle, Mail, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { callBackend, useQueryState } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { CallButton } from './CallButton';
import { buttonClass, errorMessage, formatMinutes, formatTalk, formatWhen, percent } from './format';
import type { RecoveryReport } from './types';

type Period = '7d' | '30d' | 'month' | 'lastMonth';

function periodRange(period: Period) {
  const now = new Date();
  if (period === 'month') return { fromMs: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), toMs: now.getTime() };
  if (period === 'lastMonth') {
    return { fromMs: new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime(), toMs: new Date(now.getFullYear(), now.getMonth(), 1).getTime() };
  }
  return { fromMs: now.getTime() - (period === '7d' ? 7 : 30) * 86_400_000, toMs: now.getTime() };
}

const PERIOD_LABELS: Record<Period, string> = { '7d': 'Last 7 days', '30d': 'Last 30 days', month: 'This month', lastMonth: 'Last month' };

function StatTile({ label, value, note }: { label: string; value: string | number; note?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {note && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{note}</p>}
    </div>
  );
}

type Counts = { openCallbacks: number; overdueCallbacks: number; openFollowUps: number; newCallers: number; insights: number; countsMayBeIncomplete: boolean };
type ManagementReport = {
  buckets: Array<{ fromMs: number; toMs: number; calls: number; inbound: number; missed: number }>;
  owners: Array<{ extension?: string; name: string; open: number; overdue: number }>;
  previous: { totals: RecoveryReport['totals']; recoveryRate: number | null; missedCallers: number };
  incomplete: boolean;
};

type Digest = {
  customerName: string;
  fromMs: number;
  toMs: number;
  totals: RecoveryReport['totals'];
  recovery: Omit<RecoveryReport, 'totals' | 'stillWaiting'>;
  topCompanies: Array<{ name: string; calls: number }>;
  firstTimeCallers: number;
  openCallbacks: number;
  overdueCallbacks: number;
  insights: Array<{ title: string; detail: string }>;
};

function DigestPreview({ onClose }: { onClose: () => void }) {
  const { data } = useCrm();
  const digest = useQueryState<Digest>(backendApi.crmIntegrations.getDigest, {});
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const result = await callBackend<{ ok: boolean; message: string }>(backendApi.crmIntegrations.sendDigestNow, {});
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSending(false);
    }
  };
  const d = digest.data;
  return (
    <Modal isOpen onClose={onClose} title="Weekly summary" size="lg">
      {digest.error && <p className="text-red-600">{digest.error}</p>}
      {!d && !digest.error && <p className="text-gray-500">Loading…</p>}
      {d && (
        <div className="space-y-4 text-sm text-gray-800 dark:text-gray-200">
          <p className="text-gray-500 dark:text-gray-400">
            {new Date(d.fromMs).toLocaleDateString()} – {new Date(d.toMs).toLocaleDateString()} · sent Monday mornings to {data?.settings.digestRecipients.join(', ') || 'nobody yet (add recipients in Settings)'}
          </p>
          <table className="w-full">
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {[
                ['Calls', d.totals.calls],
                ['Inbound answered', `${d.totals.answeredInbound} of ${d.totals.inbound}`],
                ['Callers whose calls were missed', d.recovery.missedCallers],
                ['… reached again', `${d.recovery.calledBack + d.recovery.callerReached} (${percent(d.recovery.recoveryRate)})`],
                ['Median time to reach them', formatMinutes(d.recovery.medianMinutesToRecover)],
                ['Never reached', d.recovery.notRecovered],
                ['Callbacks still open (overdue)', `${d.openCallbacks} (${d.overdueCallbacks})`],
                ['First-time callers', d.firstTimeCallers],
              ].map(([label, value]) => (
                <tr key={String(label)}>
                  <td className="py-1.5 text-gray-500 dark:text-gray-400">{label}</td>
                  <td className="py-1.5 text-right font-medium tabular-nums">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {d.topCompanies.length > 0 && (
            <div>
              <p className="font-medium mb-1">Busiest accounts</p>
              <ul className="list-disc pl-5">
                {d.topCompanies.map((company) => (
                  <li key={company.name}>
                    {company.name}: {company.calls} calls
                  </li>
                ))}
              </ul>
            </div>
          )}
          {d.insights.length > 0 && (
            <div>
              <p className="font-medium mb-1">Worth a look</p>
              <ul className="list-disc pl-5">
                {d.insights.map((insight) => (
                  <li key={insight.title}>
                    {insight.title}. {insight.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data?.canManage && (
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500">{data.capabilities.email ? '' : 'Email sending is not set up on the server yet.'}</p>
              <button type="button" onClick={() => void send()} disabled={sending || !data.capabilities.email} className={buttonClass.primary}>
                <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send now'}
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export function OverviewTab({ onOpenTab }: { onOpenTab: (tab: 'callbacks' | 'callers' | 'insights', owner?: string) => void }) {
  const { data, hasFeature, openContact } = useCrm();
  const [period, setPeriod] = useState<Period>('7d');
  const [showDigest, setShowDigest] = useState(false);
  const [showManagement, setShowManagement] = useState(false);
  const range = useMemo(() => periodRange(period), [period]);
  const report = useQueryState<RecoveryReport>(backendApi.crm.getRecoveryReport, range);
  const counts = useQueryState<Counts>(backendApi.crm.getCounts, {});
  const management = useQueryState<ManagementReport>(backendApi.crm.getManagementReport, showManagement ? range : 'skip');
  const r = report.data;
  const reached = r ? r.calledBack + r.callerReached : 0;
  const sla = data?.settings.callbackSlaMinutes ?? 30;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(PERIOD_LABELS) as Period[]).map((key) => (
          <button
            type="button"
            key={key}
            onClick={() => setPeriod(key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${period === key ? 'bg-primary-600 text-white' : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
          >
            {PERIOD_LABELS[key]}
          </button>
        ))}
        {hasFeature('weeklyDigest') && (
          <button type="button" onClick={() => setShowDigest(true)} className={`${buttonClass.secondary} ml-auto`}>
            <Mail className="w-4 h-4" /> Weekly summary
          </button>
        )}
        <button type="button" onClick={() => setShowManagement((shown) => !shown)} className={buttonClass.secondary}>{showManagement ? 'Hide management report' : 'Management report'}</button>
      </div>

      {showManagement && <Card animate={false}>
        <h3 className="font-semibold text-gray-900 dark:text-white">Management report</h3>
        <p className="text-xs text-gray-500 mt-1">The comparison uses the preceding period of the same length. Workload is current, regardless of the selected period.</p>
        {management.error && <p className="text-sm text-red-600 mt-2">{management.error}</p>}
        {management.isLoading && <p className="text-sm text-gray-500 mt-2">Loading report…</p>}
        {management.data && <div className="space-y-4 mt-4 text-sm">
          {management.data.incomplete && <p className="text-amber-700 dark:text-amber-400">Some results reached their limit and may be incomplete; choose a shorter period.</p>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile label="Calls, previous period" value={management.data.previous.totals.calls} note={r ? `${r.totals.calls} in selected period` : undefined} />
            <StatTile label="Missed callers, previous period" value={management.data.previous.missedCallers} note={r ? `${r.missedCallers} in selected period` : undefined} />
            <StatTile label="Recovery, previous period" value={percent(management.data.previous.recoveryRate)} note={r ? `${percent(r.recoveryRate)} in selected period` : undefined} />
          </div>
          <div className="overflow-x-auto"><table className="w-full text-left">
            <caption className="text-left font-medium mb-2">Call activity over time</caption>
            <thead className="text-gray-500"><tr><th>From</th><th className="text-right">Calls</th><th className="text-right">Inbound</th><th className="text-right">Missed inbound</th></tr></thead>
            <tbody>{management.data.buckets.map((bucket) => <tr key={bucket.fromMs} className="border-t border-gray-100 dark:border-gray-700"><td className="py-1">{new Date(bucket.fromMs).toLocaleDateString()}</td><td className="text-right">{bucket.calls}</td><td className="text-right">{bucket.inbound}</td><td className="text-right">{bucket.missed}</td></tr>)}</tbody>
          </table></div>
          <div className="overflow-x-auto"><table className="w-full text-left">
            <caption className="text-left font-medium mb-2">Open work by assignee</caption>
            <thead className="text-gray-500"><tr><th>Assignee</th><th className="text-right">Open</th><th className="text-right">Overdue</th></tr></thead>
            <tbody>{management.data.owners.map((owner) => <tr key={owner.extension ?? 'unassigned'} className="border-t border-gray-100 dark:border-gray-700"><td className="py-1"><button type="button" className="text-primary-600 hover:underline" onClick={() => onOpenTab('callbacks', owner.extension ?? '')}>{owner.name}</button></td><td className="text-right">{owner.open}</td><td className="text-right">{owner.overdue}</td></tr>)}</tbody>
          </table></div>
          <button type="button" onClick={() => onOpenTab('callbacks')} className={buttonClass.secondary}>Open tasks</button>
        </div>}
      </Card>}

      {report.error && <Card animate={false}><p className="text-red-600">{report.error}</p></Card>}
      {r?.incomplete && <p className="text-sm text-amber-700 dark:text-amber-400" role="status">This report reached its call limit; the figures may be incomplete. Choose a shorter period.</p>}
      {counts.data?.countsMayBeIncomplete && <p className="text-sm text-amber-700 dark:text-amber-400" role="status">Some workspace counts have reached their limit and may be incomplete.</p>}

      <Card animate={false}>
        {!r && !report.error ? (
          <p className="text-gray-500">Working out the numbers…</p>
        ) : r ? (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Missed callers reached again</p>
              <p className="text-5xl font-semibold text-gray-900 dark:text-white mt-1">{percent(r.recoveryRate)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {r.missedCallers === 0
                  ? 'No missed callers in this period.'
                  : `${reached.toLocaleString()} of ${(r.missedCallers - r.pending).toLocaleString()} callers whose calls were missed were reached within 7 days: ${r.calledBack.toLocaleString()} called back, ${r.callerReached.toLocaleString()} got through when they called again.${r.pending ? ` ${r.pending} more are still within the 7 days.` : ''}`}
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile label="Calls" value={r.totals.calls} note={`${r.totals.inbound.toLocaleString()} in · ${r.totals.outbound.toLocaleString()} out · ${formatTalk(r.totals.talkSeconds)} talk`} />
              <StatTile
                label="Inbound answered"
                value={r.totals.inbound ? percent(r.totals.answeredInbound / r.totals.inbound) : '—'}
                note={`${r.totals.answeredInbound.toLocaleString()} of ${r.totals.inbound.toLocaleString()}; a call counts once, however many phones rang`}
              />
              <StatTile label="Median time to reach a missed caller" value={formatMinutes(r.medianMinutesToRecover)} note={`${r.withinSla.toLocaleString()} reached within ${sla} min`} />
              <StatTile label="Never reached" value={r.notRecovered} note={`${r.missedCalls.toLocaleString()} missed calls from ${r.missedCallers.toLocaleString()} callers`} />
            </div>
          </div>
        ) : null}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {hasFeature('missedCallRecovery') && (
          <button type="button" onClick={() => onOpenTab('callbacks')} className="text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-primary-400">
            <p className="text-sm text-gray-500 dark:text-gray-400">Callbacks waiting</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">{counts.data?.openCallbacks ?? '—'}</p>
            {Boolean(counts.data?.overdueCallbacks) && (
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> {counts.data!.overdueCallbacks} overdue
              </p>
            )}
          </button>
        )}
        {hasFeature('newCallerInbox') && (
          <button type="button" onClick={() => onOpenTab('callers')} className="text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-primary-400">
            <p className="text-sm text-gray-500 dark:text-gray-400">New callers (30 days)</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">{counts.data?.newCallers ?? '—'}</p>
            <p className="text-xs text-gray-500">Name them, or mark as spam</p>
          </button>
        )}
        {hasFeature('insights') && (
          <button type="button" onClick={() => onOpenTab('insights')} className="text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-primary-400">
            <p className="text-sm text-gray-500 dark:text-gray-400">Account insights</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">{counts.data?.insights ?? '—'}</p>
            <p className="text-xs text-gray-500">Quiet customers, call spikes, owners</p>
          </button>
        )}
      </div>

      {r && r.stillWaiting.length > 0 && (
        <Card animate={false}>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Missed callers not reached yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Most recent first. Nobody has spoken to them since their call was missed.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="py-2 pr-3 font-medium">Caller</th>
                  <th className="py-2 pr-3 font-medium text-right">Missed</th>
                  <th className="py-2 pr-3 font-medium">Last missed</th>
                  <th className="py-2 pr-3 font-medium text-right">Attempts</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {r.stillWaiting.map((row) => (
                  <tr key={`${row.partyKey}-${row.firstMissedAtMs}`}>
                    <td className="py-2 pr-3">
                      {row.contactId ? (
                        <button type="button" onClick={() => openContact(row.contactId!)} className="font-medium text-gray-900 dark:text-white hover:underline text-left">
                          {row.name}
                          {row.company && <span className="font-normal text-gray-500"> · {row.company}</span>}
                        </button>
                      ) : (
                        row.name
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{row.missedCount}</td>
                    <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{formatWhen(row.lastMissedAtMs)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{row.attempts}</td>
                    <td className="py-2 text-right">{row.phone && <CallButton number={row.phone} size="sm" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {r && r.byExtension.length > 0 && (
        <Card animate={false}>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Who called missed callers back</h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {r.byExtension.map((row) => (
                <tr key={row.extension}>
                  <td className="py-2">{row.name ? `${row.name} (${row.extension})` : row.extension}</td>
                  <td className="py-2 text-right tabular-nums">{row.recovered}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {showDigest && <DigestPreview onClose={() => setShowDigest(false)} />}
    </div>
  );
}
